import {
    Landmark,
    Globe2,
    Stethoscope,
    MessagesSquare,
    HeartHandshake,
    ShieldCheck,
    FileCheck,
    Handshake,
} from "lucide-react";

export const NAV_LINKS = [
    { label: "Home", href: "#home" },
    { label: "About Us", href: "/about", route: true },
    { label: "Healthcare & Medical Tourism", href: "/healthcare-medical-tourism", route: true },
    { label: "International Trading", href: "/international-trading", route: true },
    { label: "Our Brands", href: "/our-brands", route: true },
    { label: "Global Network", href: "/global-network", route: true },
    { label: "Insights", href: "/insights", route: true },
    { label: "Contact", href: "/contact", route: true },
];

export const MARQUEE_ITEMS = [
    "Mauritius",
    "Vietnam",
    "India",
    "Thailand",
    "China",
    "Middle East",
    "Africa",
    "Europe",
];

export const IMAGES = {
    healthcare:
        "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=1400&q=70",
    surgery:
        "https://images.unsplash.com/photo-1551601651-2a8555f1a136?auto=format&fit=crop&w=800&q=70",
    containers:
        "https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?auto=format&fit=crop&w=1400&q=70",
    mauritius:
        "https://images.unsplash.com/photo-1544550581-5f7ceaf7f992?auto=format&fit=crop&w=1400&q=70",
};

export const DIVISIONS = [
    {
        id: "healthcare",
        index: "01",
        title: "Healthcare & Medical Tourism",
        copy: "We help international patients explore healthcare options, connect with suitable providers and coordinate important parts of their medical journey across Vietnam, India, Thailand and China.",
        image: IMAGES.healthcare,
        imageAlt: "International patient consulting with a healthcare specialist",
        points: [
            "Hospital and specialist connections",
            "Treatment-plan coordination",
            "International patient support",
            "Travel and visa assistance",
            "Translation and appointments",
            "Recovery and aftercare coordination",
        ],
        cta: "Explore Medical Tourism",
        ctaTarget: "#destinations",
        testid: "healthcare",
    },
    {
        id: "trading",
        index: "02",
        title: "International Trading",
        copy: "We connect qualified buyers, suppliers, distributors and commercial partners across selected energy, agricultural and food-product markets.",
        image: IMAGES.containers,
        imageAlt: "Aerial view of international shipping containers at a port",
        points: [
            "Oil and gas products",
            "Rice",
            "Dry fruits and nuts",
            "Seafood",
            "Product and specification matching",
            "Commercial and documentation coordination",
        ],
        cta: "Explore Global Trading",
        ctaTarget: "#products",
        testid: "trading",
    },
];

export const STEPS = [
    {
        n: "01",
        title: "Understand",
        copy: "We listen carefully and identify the correct division, country, service or product.",
    },
    {
        n: "02",
        title: "Connect",
        copy: "We introduce suitable healthcare providers, partners, buyers or suppliers.",
    },
    {
        n: "03",
        title: "Clarify",
        copy: "We support communication, comparisons, requirements and documentation.",
    },
    {
        n: "04",
        title: "Coordinate",
        copy: "Our international teams help move the agreed process forward.",
    },
];

export const BRANDS = [
    {
        id: "vietclinics",
        name: "VietClinics",
        logo: "/assets/logos/vietclinics-logo.png",
        website: "https://www.vietclinics.com",
        copy: "Independent healthcare coordination for international patients seeking medical and dental treatment in Vietnam.",
        full: "VietClinics is an independent healthcare coordination platform supporting international patients seeking medical and dental treatment in Vietnam. The team helps patients explore suitable providers, arrange consultations, understand treatment plans and coordinate travel, translation, appointments and aftercare.",
        services: [
            "Suitable provider exploration",
            "Consultation arrangement",
            "Treatment-plan guidance",
            "Travel and translation coordination",
            "Appointment scheduling",
            "Aftercare coordination",
        ],
    },
    {
        id: "doctors-vietnam",
        name: "Doctors Vietnam",
        logo: "/assets/logos/doctors-vietnam-logo.png",
        website: "https://www.doctorsvietnam.com",
        copy: "Urgent and general medical support for travellers, expatriates and international residents across Vietnam.",
        full: "Doctors Vietnam supports travellers, expatriates and international residents who need urgent or general medical assistance in Vietnam. Depending on location, medical need and provider availability, support may include online consultations, hotel doctor coordination, clinic appointments, hospital assistance and specialist referrals.",
        services: [
            "Online consultations",
            "Hotel doctor coordination",
            "Clinic appointments",
            "Hospital assistance",
            "Specialist referrals",
        ],
    },
    {
        id: "dental-vietnam",
        name: "Dental Vietnam",
        logo: "/assets/logos/dental-vietnam-logo.png",
        website: "https://www.dental-vietnam.com",
        copy: "Dental treatment options, provider connections and patient coordination for international patients.",
        full: "Dental Vietnam helps international patients explore dental treatment options in Vietnam, from preventive and cosmetic dentistry to dental implants and more complex treatment plans. The service supports provider introductions, treatment comparisons, appointments, travel planning and follow-up coordination.",
        services: [
            "Provider introductions",
            "Treatment comparisons",
            "Appointment coordination",
            "Travel planning",
            "Follow-up coordination",
        ],
    },
    {
        id: "plastic-surgery-vietnam",
        name: "Plastic Surgery Vietnam",
        logo: "/assets/logos/plastic-surgery-vietnam-logo.jpg",
        website: null,
        copy: "Aesthetic treatment research, consultation coordination, hospital options and recovery support.",
        full: "Plastic Surgery Vietnam supports international patients researching aesthetic and reconstructive treatment in Vietnam. The team coordinates enquiries, medical reviews, consultations, surgeon and hospital options, treatment planning, travel arrangements and recovery support.",
        services: [
            "Enquiry coordination",
            "Medical reviews",
            "Consultation arrangement",
            "Surgeon and hospital options",
            "Treatment planning",
            "Travel and recovery support",
        ],
    },
    {
        id: "swift-vietnam-visa",
        name: "Swift Vietnam Visa",
        logo: "/assets/logos/swift-vietnam-visa-logo.png",
        website: "https://www.swiftvietnamvisa.com",
        copy: "Practical visa application, urgent processing, airport and arrival assistance for travellers to Vietnam.",
        full: "Swift Vietnam Visa provides practical visa and arrival support for people travelling to Vietnam. Services may include application preparation, document review, urgent processing coordination, airport fast-track, transportation and visa-run assistance.",
        services: [
            "Application preparation",
            "Document review",
            "Urgent processing coordination",
            "Airport fast-track",
            "Transportation",
            "Visa-run assistance",
        ],
    },
    {
        id: "travel-vietnam",
        name: "Travel Vietnam",
        logo: null,
        website: null,
        copy: "Accommodation, transportation, local experiences and tailored travel arrangements across Vietnam.",
        full: "Travel Vietnam supports international visitors with accommodation, transportation, local experiences and tailored journeys across Vietnam, including practical travel arrangements for healthcare patients and accompanying family members.",
        services: [
            "Accommodation",
            "Transportation",
            "Local experiences",
            "Tailored journeys",
            "Healthcare-patient travel support",
            "Family travel arrangements",
        ],
    },
];

export const PARENT_BRAND = {
    id: "viet-medica",
    name: "Viet Medica Ltd",
    logo: "/assets/logos/viet-medica-logo.png",
    copy: "Viet Medica Ltd is the Mauritius-registered parent company connecting the group's healthcare, medical-tourism, visa, travel and international trading activities.",
};

export const COMPANY_DETAILS = [
    { id: "company", label: "Company", value: "Viet Medica Ltd" },
    { id: "registration-number", label: "Company Registration Number", value: "238949" },
    { id: "trading-as", label: "Trading As", value: "VietClinics" },
    { id: "registered-address", label: "Registered Address", value: "Sir William Newton Street, Curepipe, Mauritius" },
    { id: "directors", label: "Director", value: "Nash Kumar — Founder & Director" },
    { id: "mauritius-phone", label: "Mauritius Phone", value: "+230 57741437" },
    { id: "vietnam-phone", label: "Vietnam / HCMC Phone", value: "+84 38 6492508" },
    { id: "vietnam-location", label: "Vietnam Location", value: "Ho Chi Minh City, Vietnam" },
    { id: "corporate-email", label: "Corporate Email", value: "nash.k@vietclinics.com" },
];

export const ABOUT_NETWORK = [
    { name: "Mauritius", role: "Registered corporate base" },
    { name: "Vietnam", role: "Healthcare, visa and travel services" },
    { name: "India", role: "Hospital and medical-tourism partnerships" },
    { name: "Thailand", role: "Healthcare and medical-tourism connections" },
    { name: "China", role: "Healthcare and commercial connections" },
    {
        name: "Africa, the Middle East & Europe",
        role: "International partnerships and trading relationships",
    },
];

export const VALUES = [
    { title: "Trust", copy: "We build relationships carefully and responsibly." },
    { title: "Clarity", copy: "We explain roles, requirements and next steps." },
    { title: "Human Support", copy: "Clients communicate with real people." },
    { title: "International Perspective", copy: "We understand different markets and cultures." },
    { title: "Professional Coordination", copy: "We help the right parties work together." },
    { title: "Long-Term Relationships", copy: "We focus on sustainable partnerships." },
];

export const TEAM_GROUPS = [
    "Directors",
    "International Healthcare Team",
    "Medical Advisers",
    "Patient Coordinators",
    "Country Representatives",
    "Trading & Business-Development Team",
];

export const TESTIMONIAL_TABS = [
    {
        id: "patient",
        label: "International Patient Reviews",
        alias: "Verified Patient",
        empty: "Genuine patient reviews are being gathered with written permission and will be published here once received and verified.",
    },
    {
        id: "partner",
        label: "Healthcare Partner Reviews",
        alias: "Healthcare Partner",
        empty: "Feedback from doctors, clinics and hospitals will be published here once genuine, permissioned testimonials are received.",
    },
    {
        id: "trading",
        label: "Trading and Sourcing Partner Reviews",
        alias: "International Business Partner",
        empty: "Feedback from buyers, suppliers, distributors and business partners will be published here once genuine, permissioned testimonials are received.",
    },
];

export const TESTIMONIAL_NOTE =
    "Testimonials reflect individual experiences and do not guarantee medical, commercial or service outcomes.";

export const PRODUCTS = [
    {
        id: "oil-gas",
        title: "Oil & Gas Products",
        image: "https://images.unsplash.com/photo-1516937941344-00b4e0337589?auto=format&fit=crop&w=1000&q=70",
        imageAlt: "Oil refinery and petrochemical plant complex",
        copy: "Selected petroleum, fuel and related energy-product opportunities, subject to product availability, counterparty verification and compliance.",
        points: [
            "Buyer and seller introductions",
            "Product and specification requirements",
            "Quantities and delivery schedules",
            "Country of origin",
            "Destination and discharge-port requirements",
            "Commercial-document coordination",
            "Inspection requirements",
            "Contract and compliance procedures",
        ],
    },
    {
        id: "rice",
        title: "Rice",
        image: "https://images.unsplash.com/photo-1586201375761-83865001e31c?auto=format&fit=crop&w=1000&q=70",
        imageAlt: "Premium rice grains prepared for international export",
        copy: "We connect international buyers with suitable rice suppliers based on variety, grade, broken percentage, packaging, certifications, order volume and destination.",
        points: [
            "White, fragrant and other confirmed rice varieties",
            "Grade and broken percentage",
            "Bulk and retail packaging",
            "Private-label requirements",
            "Product samples",
            "Certifications",
            "Order quantities",
            "Delivery frequency",
            "Destination ports",
            "Export documentation",
        ],
    },
    {
        id: "dry-fruits-nuts",
        title: "Dry Fruits & Nuts",
        image: "https://images.unsplash.com/photo-1508061253366-f7da158b6d46?auto=format&fit=crop&w=1000&q=70",
        imageAlt: "Whole premium almonds prepared for export",
        copy: "We support international sourcing opportunities for selected dry fruits, nuts and related food products.",
        points: [
            "Selected dried fruits and nuts",
            "Product origin and grade",
            "Processing requirements",
            "Bulk or retail packaging",
            "Private-label enquiries",
            "Certifications",
            "Samples",
            "Importer and distributor connections",
        ],
    },
    {
        id: "seafood",
        title: "Seafood",
        image: "https://images.unsplash.com/photo-1615141982883-c7ad0e69fd62?auto=format&fit=crop&w=1000&q=70",
        imageAlt: "Fresh fish, shellfish and seafood arranged on crushed ice",
        copy: "We connect buyers and suppliers across selected fresh, frozen and processed seafood categories.",
        points: [
            "Fresh, frozen and processed seafood",
            "Species and country of origin",
            "Product size and processing",
            "Packaging",
            "Cold-chain requirements",
            "Health and export certification",
            "Inspection",
            "Order volume",
            "Destination-market requirements",
        ],
    },
];

export const BUYER_STEPS = [
    "Receive the detailed product requirement",
    "Verify the buyer and company",
    "Confirm specifications, quantity and destination",
    "Identify suitable supply connections",
    "Coordinate quotations and approved documents",
    "Support communication through due diligence and contracting",
];

export const SUPPLIER_STEPS = [
    "Review the company and product information",
    "Verify authority and available documentation",
    "Confirm product specifications and market suitability",
    "Match genuine buyer requirements",
    "Coordinate introductions and commercial communication",
    "Support long-term market relationships",
];

export const TRADING_STATEMENT =
    "Viet Medica Ltd provides introductions and international business coordination. All opportunities remain subject to counterparty verification, product availability, confirmed specifications, samples where applicable, contracts, compliance checks, payment security and applicable law.";

export const LEGAL_DOCS = {
    "privacy-policy": {
        title: "Privacy Policy",
        published: false,
        body: [
            "This document is being finalised by our legal advisers and will be published on this page once approved.",
            "If you have a question about how Viet Medica Ltd handles personal information in the meantime, please contact our team through the enquiry section of this website.",
        ],
    },
    "terms-and-conditions": {
        title: "Terms and Conditions",
        published: false,
        body: [
            "This document is being finalised by our legal advisers and will be published on this page once approved.",
            "If you have a question about the terms governing the use of this website in the meantime, please contact our team through the enquiry section.",
        ],
    },
    "cookie-policy": {
        title: "Cookie Policy",
        published: false,
        body: [
            "This document is being finalised by our legal advisers and will be published on this page once approved.",
            "If you have a question about cookies used on this website in the meantime, please contact our team through the enquiry section.",
        ],
    },
    "healthcare-disclaimer": {
        title: "Healthcare Disclaimer",
        published: true,
        body: [
            "Viet Medica Ltd is an independent healthcare and medical-tourism coordinator. We do not diagnose, prescribe, guarantee outcomes or replace licensed healthcare professionals. Treatment recommendations, suitability, consent and clinical decisions remain with the treating provider.",
            "Medical diagnosis, treatment decisions and patient suitability remain the responsibility of licensed healthcare professionals and providers. Visa and immigration decisions remain with the relevant authorities.",
        ],
    },
    "trading-disclaimer": {
        title: "Trading Disclaimer",
        published: true,
        body: [
            "All opportunities are subject to counterparty verification, product availability, agreed specifications, samples where applicable, contracts, compliance checks and relevant international and local laws. Information displayed on this website does not constitute a binding offer.",
            "Viet Medica Ltd provides introductions and international business coordination and does not act as a producer, refinery, manufacturer, vessel owner or guaranteed supplier.",
        ],
    },
};

export const DESTINATIONS = [
    {
        id: "vietnam",
        name: "Vietnam",
        image: "https://images.unsplash.com/photo-1528127269322-539801943592?auto=format&fit=crop&w=900&q=70",
        imageAlt: "Limestone karsts of Ha Long Bay, Vietnam",
        copy: "Healthcare coordination, dental treatment, aesthetic care, urgent medical assistance, visa support and recovery coordination.",
    },
    {
        id: "india",
        name: "India",
        image: "https://images.unsplash.com/photo-1524492412937-b28074a5d7da?auto=format&fit=crop&w=900&q=70",
        imageAlt: "The Taj Mahal at sunrise, India",
        copy: "Connections with established hospital groups, specialist departments and multi-specialty treatment pathways.",
    },
    {
        id: "thailand",
        name: "Thailand",
        image: "https://images.unsplash.com/photo-1508009603885-50cf7c579365?auto=format&fit=crop&w=900&q=70",
        imageAlt: "Temple skyline of Bangkok, Thailand",
        copy: "International hospital services, dental care, wellness, aesthetic treatment and established medical-tourism infrastructure.",
    },
    {
        id: "china",
        name: "China",
        image: "https://images.unsplash.com/photo-1508804185872-d7badad00f7d?auto=format&fit=crop&w=900&q=70",
        imageAlt: "The Great Wall of China crossing mountain ridges",
        copy: "Specialist hospital access and advanced treatment opportunities supported by careful cross-border communication.",
    },
];

export const REGIONS = [
    { name: "Mauritius", role: "Registered corporate base" },
    { name: "Vietnam", role: "Healthcare, visa and travel operations" },
    { name: "India", role: "Medical-tourism and hospital partnerships" },
    { name: "Thailand", role: "Healthcare and medical-tourism network" },
    { name: "China", role: "Healthcare and commercial connections" },
    {
        name: "Africa, Middle East & Europe",
        role: "International business relationships",
    },
];

export const TRUST_POINTS = [
    { icon: Landmark, label: "Mauritius-registered international company" },
    { icon: Globe2, label: "International teams and partner network" },
    { icon: Stethoscope, label: "Specialist healthcare and trading divisions" },
    { icon: MessagesSquare, label: "Clear communication across borders" },
    { icon: HeartHandshake, label: "Human coordination" },
    { icon: ShieldCheck, label: "Responsible partner selection" },
    { icon: FileCheck, label: "Transparent processes and next steps" },
    { icon: Handshake, label: "Long-term international relationships" },
];

export const FOOTER_DISCLAIMER =
    "Viet Medica Ltd provides independent coordination and international business support. Medical diagnosis, treatment decisions and patient suitability remain the responsibility of licensed healthcare professionals and providers. Visa and immigration decisions remain with the relevant authorities. Trading opportunities remain subject to verification, availability, contractual agreement, compliance and applicable law.";

export const FINAL_DISCLAIMER =
    "Viet Medica Ltd provides independent healthcare coordination, international business introductions and sourcing support. We do not manufacture products, provide medical treatment, guarantee supply or act as a government authority. All healthcare decisions remain with licensed providers. All trading and sourcing opportunities remain subject to buyer and supplier verification, product availability, confirmed specifications, samples where applicable, contracts, inspections, compliance, payment security and applicable laws.";

export const HEALTHCARE_SERVICES = [
    "Initial enquiry and case-summary coordination",
    "Suitable hospital, clinic or specialist connections",
    "Treatment-plan and quotation coordination",
    "Online consultations where available",
    "Medical-document sharing",
    "English-speaking coordination",
    "Translation support",
    "Visa and travel assistance",
    "Airport and local transportation",
    "Hospital admission and appointment support",
    "Accommodation assistance",
    "Nursing and recovery support where arranged",
    "Follow-up communication",
];

export const PATIENT_JOURNEY = [
    "Tell us what support you need",
    "Share available reports or medical information",
    "Receive suitable provider options",
    "Arrange consultations",
    "Review the provider's treatment plan and quotation",
    "Confirm travel and appointments",
    "Receive coordinated support during the journey",
    "Continue with provider-led follow-up and aftercare",
];

export const TRADING_SUPPORT = [
    "Buyer and supplier introductions",
    "Initial counterparty qualification",
    "Product and specification matching",
    "Quantity and delivery requirement alignment",
    "Samples and quotations",
    "Commercial-document coordination",
    "Communication across countries and time zones",
    "Coordination with inspection, logistics, finance and legal professionals where required",
    "Development of long-term supplier and distribution relationships",
];

export const TRADING_PROCESS = [
    "Submit a clear requirement",
    "Complete company and identity verification",
    "Confirm product specifications and quantities",
    "Identify appropriate counterparties",
    "Exchange approved commercial documentation",
    "Review samples, quotations and terms",
    "Complete independent legal and compliance checks",
    "Finalise contracts and transaction arrangements",
];

export const COMPLIANCE_ITEMS = [
    "Company verification",
    "Buyer and seller authority",
    "KYC and AML checks",
    "Sanctions and restricted-party screening",
    "Product and origin verification",
    "Import and export requirements",
    "Independent inspection where required",
    "Contract review",
    "Payment-security procedures",
    "Anti-bribery expectations",
    "Data confidentiality",
];

export const HEALTHCARE_DISCLAIMER =
    "Viet Medica Ltd is an independent healthcare and medical-tourism coordinator. We do not diagnose, prescribe, guarantee outcomes or replace licensed healthcare professionals. Treatment recommendations, suitability, consent and clinical decisions remain with the treating provider.";

export const TRADING_DISCLAIMER =
    "All opportunities are subject to counterparty verification, product availability, agreed specifications, samples where applicable, contracts, compliance checks and relevant international and local laws. Information displayed on this website does not constitute a binding offer.";

export const CONTACT_CHANNELS = [
    { id: "corporate-email", label: "Corporate Email", value: "nash.k@vietclinics.com", href: "mailto:nash.k@vietclinics.com" },
    { id: "mauritius-phone", label: "Mauritius Phone", value: "+230 57741437", href: "tel:+23057741437" },
    { id: "vietnam-phone", label: "Vietnam / HCMC Phone", value: "+84 38 6492508", href: "tel:+84386492508" },
    { id: "whatsapp", label: "WhatsApp", value: null },
    { id: "registered-address", label: "Mauritius Registered Address", value: "Sir William Newton Street, Curepipe, Mauritius" },
    { id: "registration-number", label: "Company Registration Number", value: "238949" },
];

export const BRAND_WHO = {
    vietclinics: "International patients seeking medical or dental treatment in Vietnam.",
    "doctors-vietnam": "Travellers, expatriates and international residents in Vietnam.",
    "dental-vietnam": "International patients exploring dental treatment options in Vietnam.",
    "plastic-surgery-vietnam": "International patients researching aesthetic or reconstructive treatment in Vietnam.",
    "swift-vietnam-visa": "Travellers who need practical visa and arrival support for Vietnam.",
    "travel-vietnam": "International visitors, healthcare patients and accompanying family members travelling in Vietnam.",
};

export const VN_AGENT_SERVICES = [
    "Product and supplier searches",
    "Initial supplier communication",
    "Company and document checks",
    "Product specifications",
    "Samples and quotations",
    "Factory or supplier visit coordination",
    "Packaging and private-label requirements",
    "Quality inspection coordination",
    "Export-document support",
    "Freight and logistics coordination",
    "Destination-port requirements",
    "Ongoing buyer and supplier communication",
];

export const VN_PROCESS = [
    { title: "Submit Your Requirement", copy: "Tell us the product, specification, quantity, packaging, target price range and destination." },
    { title: "Initial Buyer Verification", copy: "We review the buyer, company and commercial requirement before approaching potential suppliers." },
    { title: "Supplier Search and Communication", copy: "Our Vietnam agents contact suitable supplier connections and clarify the requirement." },
    { title: "Samples and Quotations", copy: "Where available, we coordinate product information, samples, quotations and supporting documents." },
    { title: "Due Diligence and Inspection", copy: "Buyers should complete independent legal, financial and product due diligence. We can help coordinate with relevant professional providers where required." },
    { title: "Contract and Shipment Planning", copy: "The buyer and supplier agree on contracts, payment, delivery and shipping arrangements." },
    { title: "Ongoing Coordination", copy: "Our team supports communication between the parties through the agreed process." },
];

export const COUNTRIES = [
    {
        id: "vietnam",
        name: "Vietnam",
        path: "/medical-tourism-vietnam",
        image: "https://images.unsplash.com/photo-1528127269322-539801943592?auto=format&fit=crop&w=1400&q=70",
        imageAlt: "Limestone karsts of Ha Long Bay, Vietnam",
        why: "Vietnam combines modern private hospitals in its major cities with experienced specialists and a growing reputation for dental, aesthetic and general medical care. International patient services continue to develop quickly, and English-speaking coordination is increasingly available in leading facilities.",
        treatments: [
            "Dental treatment and implants",
            "Aesthetic and reconstructive procedures",
            "General and urgent medical care",
            "Health screening and diagnostics",
            "Specialist consultations",
            "Recovery and rehabilitation support",
        ],
        providers: [
            "International private hospitals",
            "Specialist clinics and dental centres",
            "University and teaching hospitals",
            "Aesthetic and wellness centres",
        ],
        coordination: "Viet Medica coordinates enquiries through its Vietnam-based teams and specialist brands. We help patients share medical information securely, compare suitable provider options, arrange consultations and plan travel, visas, accommodation and aftercare.",
        travel: [
            "Visa support through Swift Vietnam Visa",
            "Airport arrival and local transportation",
            "Accommodation near treating providers",
            "Translation and appointment support",
        ],
        questions: [
            "Which treatment options are suitable for my condition?",
            "What does the treatment plan and quotation include?",
            "How many visits, or how long a stay, is required?",
            "What aftercare and follow-up support is available?",
            "Which medical documents should I bring from my home country?",
        ],
        seoTitle: "Medical Tourism in Vietnam | International Patient Coordination — Viet Medica Ltd",
        seoDesc: "Explore medical tourism in Vietnam with coordinated hospital connections, dental and aesthetic care options, visa support and recovery planning from Viet Medica Ltd.",
    },
    {
        id: "india",
        name: "India",
        path: "/medical-tourism-india",
        image: "https://images.unsplash.com/photo-1524492412937-b28074a5d7da?auto=format&fit=crop&w=1400&q=70",
        imageAlt: "The Taj Mahal at sunrise, India",
        why: "India is one of the world's most established medical-travel destinations, with large multi-specialty hospital groups, internationally trained specialists and long experience treating overseas patients across both complex and elective procedures.",
        treatments: [
            "Cardiac and orthopaedic treatment pathways",
            "Oncology and transplant programmes",
            "Fertility and women's health",
            "Neurology and spine care",
            "Gastroenterology",
            "Preventive health screening",
        ],
        providers: [
            "Multi-specialty hospital groups",
            "Specialist institutes and centres",
            "Private hospitals with international patient departments",
            "Rehabilitation facilities",
        ],
        coordination: "We help patients present their case clearly to suitable Indian hospital groups, coordinate medical opinions and quotations, support medical-visa documentation and align travel dates with admission schedules.",
        travel: [
            "Medical-visa guidance and documentation",
            "Airport pickup coordination",
            "Hotel and guest-house options near hospitals",
            "Interpreter arrangement where required",
        ],
        questions: [
            "Which hospitals have dedicated international patient departments?",
            "How are treatment estimates calculated and itemised?",
            "What is the expected admission-to-discharge timeline?",
            "How are complications or extended stays handled?",
            "What remote follow-up is offered after I return home?",
        ],
        seoTitle: "Medical Tourism in India | Hospital Connections & Patient Support — Viet Medica Ltd",
        seoDesc: "Connect with established Indian hospital groups and specialist departments through Viet Medica Ltd's international patient coordination and medical-tourism support.",
    },
    {
        id: "thailand",
        name: "Thailand",
        path: "/medical-tourism-thailand",
        image: "https://images.unsplash.com/photo-1508009603885-50cf7c579365?auto=format&fit=crop&w=1400&q=70",
        imageAlt: "Bangkok temple district at dusk, Thailand",
        why: "Thailand pairs an established private-hospital sector with a strong hospitality culture. International patient centres, wellness and dental services and smooth visitor logistics make it a long-standing destination for medical travellers.",
        treatments: [
            "Wellness and preventive health programmes",
            "Dental care and oral surgery",
            "Aesthetic and dermatological treatment",
            "Orthopaedics and sports medicine",
            "Comprehensive health screening",
        ],
        providers: [
            "International hospitals with patient centres",
            "Specialist dental and aesthetic clinics",
            "Wellness and rehabilitation resorts",
            "Private hospitals with international accreditation programmes",
        ],
        coordination: "Viet Medica helps patients compare suitable Thai hospital and clinic options, coordinate consultations and treatment plans, and align travel, recovery time and follow-up communication before and after the visit.",
        travel: [
            "Frequent regional flight connections",
            "Hospital-affiliated hotels and recovery accommodation",
            "Visa guidance for medical travellers",
            "Local transport and interpretation support",
        ],
        questions: [
            "What experience does the provider have with international patients?",
            "Which parts of the treatment can be completed remotely?",
            "What recovery time should I plan before flying home?",
            "Are itemised quotations available in English?",
            "How is follow-up care coordinated with my doctor at home?",
        ],
        seoTitle: "Medical Tourism in Thailand | Hospitals, Dental & Wellness — Viet Medica Ltd",
        seoDesc: "Coordinate medical tourism in Thailand — international hospital services, dental care, wellness and aesthetic treatment with practical travel support from Viet Medica Ltd.",
    },
    {
        id: "china",
        name: "China",
        path: "/medical-tourism-china",
        image: "https://images.unsplash.com/photo-1508804185872-d7badad00f7d?auto=format&fit=crop&w=1400&q=70",
        imageAlt: "The Great Wall of China crossing mountain ridges",
        why: "China offers access to major specialist hospitals and advanced treatment programmes, particularly in large metropolitan centres, supported by significant investment in medical technology, research and hospital infrastructure.",
        treatments: [
            "Advanced diagnostics and imaging",
            "Oncology and haematology programmes",
            "Cardiovascular care",
            "Integrative and traditional medicine alongside conventional care",
            "Complex surgical pathways",
        ],
        providers: [
            "Large metropolitan specialist hospitals",
            "University-affiliated teaching hospitals",
            "International departments within major hospitals",
        ],
        coordination: "We support careful cross-border communication for China enquiries: preparing medical documents for review, coordinating translations, arranging remote opinions where available and planning the practical details of treatment and travel.",
        travel: [
            "Visa category guidance for medical travel",
            "Translation of medical records",
            "Accommodation near treating hospitals",
            "Local coordination support in the treatment city",
        ],
        questions: [
            "How will my medical records be translated and reviewed?",
            "Is a remote consultation possible before travelling?",
            "What is the expected length of stay for my treatment?",
            "Who accompanies international patients during admission?",
            "How are reports shared with my doctors at home afterwards?",
        ],
        seoTitle: "Medical Tourism in China | Specialist Hospital Access — Viet Medica Ltd",
        seoDesc: "Access specialist hospitals and advanced treatment opportunities in China with careful cross-border communication and coordination support from Viet Medica Ltd.",
    },
];

export const PRODUCT_PAGES = [
    {
        id: "oil-gas",
        path: "/oil-gas-products",
        crumbsLabel: "Oil & Gas Products",
        title: "Oil & Gas Products",
        eyebrow: "International Trading",
        image: "https://images.unsplash.com/photo-1516937941344-00b4e0337589?auto=format&fit=crop&w=1400&q=70",
        imageAlt: "Oil refinery and petrochemical plant complex",
        intro: "Viet Medica Ltd supports introductions and commercial coordination for selected petroleum, fuel and related energy products. We help qualified buyers and sellers clarify requirements before detailed commercial discussions begin.",
        points: [
            "Buyer and seller introductions",
            "Product and specification requirements",
            "Quantities and delivery schedules",
            "Country of origin",
            "Destination and discharge-port requirements",
            "Commercial-document coordination",
            "Inspection requirements",
            "Contract and compliance procedures",
        ],
        note: "Viet Medica Ltd does not claim ownership of refineries, storage facilities, vessels or existing product allocations. Product grades, prices, quantities and availability are confirmed only through verified documentation during the transaction process.",
        enquiryPath: "/trading-enquiry",
        enquiryLabel: "Submit a Trading Enquiry",
        seoTitle: "Oil & Gas Product Trading | Buyer & Seller Introductions — Viet Medica Ltd",
        seoDesc: "Commercial coordination for selected petroleum and energy products — buyer and seller introductions, specifications, documentation and compliance workflow with Viet Medica Ltd.",
    },
    {
        id: "rice-vietnam",
        path: "/rice-export-from-vietnam",
        crumbsLabel: "Rice from Vietnam",
        title: "Rice from Vietnam",
        eyebrow: "Products from Vietnam",
        image: "https://images.unsplash.com/photo-1586201375761-83865001e31c?auto=format&fit=crop&w=1400&q=70",
        imageAlt: "Premium Vietnamese rice grains prepared for export",
        intro: "We connect international buyers with suitable Vietnamese rice suppliers based on variety, grade, broken percentage, order quantity, packaging, certifications and destination requirements.",
        points: [
            "White rice",
            "Fragrant rice",
            "Jasmine rice",
            "Other confirmed varieties",
            "Bulk packaging",
            "Retail packaging",
            "Private-label enquiries",
            "Product samples",
            "Export documentation",
            "Regular supply enquiries",
        ],
        note: "Prices and guaranteed availability are not published. All supply is confirmed only through supplier quotations, verified documentation and agreed contracts.",
        enquiryPath: "/vietnam-sourcing-enquiry",
        enquiryLabel: "Send Product Requirement",
        seoTitle: "Rice Export from Vietnam | Vietnamese Rice Supplier Connections — Viet Medica Ltd",
        seoDesc: "Source white, fragrant and jasmine rice from Vietnam. Viet Medica Ltd connects buyers with suitable Vietnamese rice suppliers, samples, packaging and export documentation support.",
    },
    {
        id: "seafood-vietnam",
        path: "/seafood-suppliers-vietnam",
        crumbsLabel: "Vietnamese Seafood",
        title: "Vietnamese Seafood",
        eyebrow: "Products from Vietnam",
        image: "https://images.unsplash.com/photo-1615141982883-c7ad0e69fd62?auto=format&fit=crop&w=1400&q=70",
        imageAlt: "Fresh fish and shellfish arranged on crushed ice for export",
        intro: "We support buyer and supplier connections for selected Vietnamese seafood products, subject to species, processing, packaging, certification and destination-market requirements.",
        points: [
            "Fresh seafood",
            "Frozen seafood",
            "Processed seafood",
            "Species and origin",
            "Product size",
            "Packaging",
            "Cold-chain requirements",
            "Health certificates",
            "Inspection coordination",
            "Destination-market requirements",
        ],
        note: "Availability, specifications and certification are confirmed per enquiry through verified suppliers and agreed documentation.",
        enquiryPath: "/vietnam-sourcing-enquiry",
        enquiryLabel: "Send Product Requirement",
        seoTitle: "Vietnamese Seafood Exporter Connections | Frozen Seafood Supplier Vietnam — Viet Medica Ltd",
        seoDesc: "Connect with Vietnamese seafood suppliers for fresh, frozen and processed products — species, cold-chain, health certification and inspection coordination via Viet Medica Ltd.",
    },
    {
        id: "dry-fruits-vietnam",
        path: "/dry-fruits-agricultural-products-vietnam",
        crumbsLabel: "Dry Fruits & Agricultural Products",
        title: "Dry Fruits, Nuts & Agricultural Products",
        eyebrow: "Products from Vietnam",
        image: "https://images.unsplash.com/photo-1508061253366-f7da158b6d46?auto=format&fit=crop&w=1400&q=70",
        imageAlt: "Premium nuts and agricultural products prepared for export",
        intro: "We help international buyers explore sourcing opportunities for selected dried fruits, nuts and agricultural products from Vietnam.",
        points: [
            "Product type",
            "Country and region of origin",
            "Grade and processing",
            "Bulk supply",
            "Retail packaging",
            "Private-label packaging",
            "Samples",
            "Certifications",
            "Importer and distributor requirements",
        ],
        note: "Product availability and specifications are confirmed per enquiry through verified suppliers and agreed documentation.",
        enquiryPath: "/vietnam-sourcing-enquiry",
        enquiryLabel: "Send Product Requirement",
        seoTitle: "Dry Fruit Supplier Vietnam | Agricultural Products from Vietnam — Viet Medica Ltd",
        seoDesc: "Source dried fruits, nuts and agricultural products from Vietnam — grade, processing, private-label packaging, samples and certifications coordinated by Viet Medica Ltd.",
    },
];
